import React from 'react';

import classnames from 'classnames/bind';
import { AlertTriangle, FileText, Upload, Clock } from 'lucide-react';

import useUnpublishedChangesWarningModal from '@HOOKS/store/modals/useUnpublishedChangesWarningModal';
import usePublishDocumentModal from '@HOOKS/store/modals/usePublishDocumentModal';

import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import BaseModal from '@MODALS/BaseModal';

import styles from './UnpublishedChangesWarningModal.module.scss';

const cx: CX = classnames.bind(styles);

function UnpublishedChangesWarningModal() {
    const {
        state: { isOpen, data: modalData },
        actions: { close },
    } = useUnpublishedChangesWarningModal();

    const {
        actions: publishDocumentModalActions,
    } = usePublishDocumentModal();

    if (!isOpen || !modalData) {
        return null;
    }

    const { documentName, lastOpenedDays, documentId } = modalData;

    const handlePublish = () => {
        // Close the warning modal
        close();
        
        // Open the publish modal  
        publishDocumentModalActions.open({
            documentData: {
                id: documentId,
                name: documentName,
                owner: { id: 0, fullName: '', email: '' },
                description: '',
                introduction_text: { body: '' },
                isPublished: false,
                isVisible: true,
                cover: null,
                chapters: [],
                users: [],
            },
        });
    };

    const handleDismiss = () => {
        // Store in localStorage that user dismissed for this document
        const dismissKey = `unpublished_warning_dismissed_${documentId}`;
        const dismissTime = new Date().getTime();
        localStorage.setItem(dismissKey, dismissTime.toString());
        
        close();
    };

    const handleClose = () => {
        close();
    };

    return (
        <BaseModal
            style={{
                top: '25%',
                transform: 'translate(-50%, -25%)',
                width: '90%',
                maxWidth: '520px',
            }}
            onClose={handleClose}
        >
            <div className={cx('unpublished-warning-modal')}>
                {/* Header with warning icon */}
                <div className={cx('header')}>
                    <div className={cx('icon-wrapper')}>
                        <AlertTriangle size={32} />
                    </div>
                    <div className={cx('title-section')}>
                        <h2 className={cx('title')}>Unpublished Changes Detected</h2>
                        <p className={cx('subtitle')}>Changes won&apos;t appear in generated documents</p>
                    </div>
                </div>

                {/* Content with document info and explanation */}
                <div className={cx('content')}>
                    <div className={cx('document-info')}>
                        <div className={cx('document-icon')}>
                            <FileText size={20} />
                        </div>
                        <div className={cx('document-details')}>
                            <div className={cx('document-name')}>{documentName}</div>
                            <div className={cx('last-opened')}>
                                <Clock size={14} />
                                <span>Last opened {lastOpenedDays} days ago</span>
                            </div>
                        </div>
                    </div>

                    <div className={cx('warning-content')}>
                        <div className={cx('warning-box')}>
                            <div className={cx('warning-header')}>
                                <AlertTriangle size={18} />
                                <span>Important Notice</span>
                            </div>
                            <p className={cx('warning-text')}>
                                This document contains unpublished changes that won&apos;t be reflected in generated documents. 
                                To ensure all updates appear in your final documents, please publish these changes.
                            </p>
                        </div>

                        <div className={cx('features-list')}>
                            <div className={cx('feature-item')}>
                                <Upload size={16} />
                                <span>Click &quot;Publish All Changes&quot; to make updates live</span>
                            </div>
                            <div className={cx('feature-item')}>
                                <FileText size={16} />
                                <span>Published changes will appear in all generated documents</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action buttons */}
                <div className={cx('actions')}>
                    <BasicButton
                        title="Remind Me Later"
                        variant="neutral"
                        size="medium"
                        onClick={handleDismiss}
                        style={{ flex: 1 }}
                    />
                    <BasicButton
                        title="Publish All Changes"
                        variant="warning"
                        size="medium"
                        onClick={handlePublish}
                        style={{ flex: 2 }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

export default UnpublishedChangesWarningModal; 